'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  supportApi,
  type CreateTicketPayload,
  type SupportStatus,
} from '../services/support-api';

const KEY = ['support', 'tickets'] as const;

export function useSupportTickets(status?: SupportStatus, limit?: number) {
  return useQuery({
    queryKey: [...KEY, { status, limit }],
    queryFn: () => supportApi.list(status, limit),
    // Lista reflectă răspunsurile agentului fără reload manual (tab activ).
    refetchInterval: 30_000,
  });
}

export function useSupportTicket(id: string | undefined | null) {
  return useQuery({
    queryKey: [...KEY, id],
    queryFn: () => supportApi.get(id!),
    enabled: !!id,
    // Firul deschis primește mesajele agentului live (înainte nu se
    // actualiza deloc până la refocus/remount). Doar cu tab-ul activ —
    // refetchIntervalInBackground rămâne false (default).
    refetchInterval: 15_000,
  });
}

export function useSupportOpenCount() {
  return useQuery({
    queryKey: [...KEY, 'open-count'],
    queryFn: () => supportApi.openCount(),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useCreateSupportTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTicketPayload) => supportApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useReplySupportTicket(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => supportApi.reply(ticketId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...KEY, ticketId] });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
